import React, { useEffect, useMemo, useState, useCallback } from "react";

import { useProductos } from "../hooks/useProductos";
import { planAgregadoAlCarrito, useCarrito } from "../hooks/useCarrito";
import { useLocalPortal } from "../hooks/useLocalPortal";
import { useTemaEstacionalCarta } from "../hooks/useTemaEstacionalCarta";
import { useCartaDigitalLogica } from "./CartaDigitalLogica";
import { fireSwal } from "../utils/swal";
import { leerSucursalIdPersistida } from "../utils/sucursalPersistencia";
import {
  normalizarNombreCategoria,
  isProductoConTamanos,
  productoRequiereSelector,
  resolverProductoSucursalId,
  validarCarritoOpciones,
} from "../mappers/productoMapper";

import CartaDigitalVista from "./CartaDigitalVista";
import ModalConfirmacionDomicilio from "./ModalDelivery";
import ModalRetiro from "./ModalRetiro";
import ModalSucursal from "./ModalSucursal";
import PedidoConfirmado from "./PedidoConfirmado";
import EstadoLocalPortal from "./EstadoLocalPortal";
import {
  resolverVistaPortalInicial,
  guardarVistaPortal,
} from "../services/trackingService";

export default function CartaDigital() {
  const [vistaPortal, setVistaPortal] = useState(resolverVistaPortalInicial);
  const [categoriaActiva, setCategoriaActiva] = useState("Todas");
  const [busqueda, setBusqueda] = useState("");
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [indiceEdicionOpciones, setIndiceEdicionOpciones] = useState(null);

  // modales
  const [mostrarModalDelivery, setMostrarModalDelivery] = useState(false);
  const [mostrarModalRetiro, setMostrarModalRetiro] = useState(false);
  const [mostrarModalSucursal, setMostrarModalSucursal] = useState(false);

  const { slug, local, estado: estadoLocal, error: errorLocal } = useLocalPortal();
  const {
    carrito,
    agregar,
    eliminar,
    actualizarComentario,
    actualizarItemDesdeCatalogo,
    actualizarCantidad,
    vaciar,
  } = useCarrito();
  const sucursalIdPersistida = useMemo(
    () => (slug ? leerSucursalIdPersistida(slug) : null),
    [slug]
  );

  const {
    ubicacionCliente,
    sucursales,
    sucursalSeleccionada,
    seleccionarSucursalManual,
    modoEntrega,
    setModoEntrega,
    costoDelivery,
    horariosDisponibles,
    horarioSeleccionado,
    setHorarioSeleccionado,
    pedidosCerrados,
    mensajeCierre,
    finalizarPedido,
    enviandoPedido,
    inicializandoSucursal,
  } = useCartaDigitalLogica(carrito, vaciar, {
    localId: estadoLocal === "ok" ? local?.id : null,
    slug,
  });

  const sucursalIdCatalogo =
    estadoLocal === "ok"
      ? sucursalSeleccionada?.id || sucursalIdPersistida
      : null;

  const {
    productos,
    categorias,
    extras,
    cargando: cargandoProductos,
  } = useProductos(sucursalIdCatalogo, estadoLocal === "ok" ? local?.id : null);

  const { temaEstacional } = useTemaEstacionalCarta(sucursalSeleccionada?.id);

  const categoriasDisponibles = useMemo(() => {
    const nombres = (categorias || []).map((cat) => String(cat.nombre || "").trim()).filter(Boolean);
    return ["Todas", ...nombres];
  }, [categorias]);

  useEffect(() => {
    setCategoriaActiva("Todas");
  }, [sucursalSeleccionada?.id]);

  useEffect(() => {
    if (!categoriasDisponibles.includes(categoriaActiva)) {
      setCategoriaActiva("Todas");
    }
  }, [categoriasDisponibles, categoriaActiva]);

  const abrirModal = useCallback((producto) => {
    setIndiceEdicionOpciones(null);
    setProductoSeleccionado(producto);
    setModalAbierto(true);
  }, []);

  const cerrarModal = useCallback(() => {
    setModalAbierto(false);
    setProductoSeleccionado(null);
    setIndiceEdicionOpciones(null);
  }, []);

  /* ================= NAVEGACIÓN PORTAL ================= */

  const irACarta = useCallback(() => {
    setVistaPortal("carta");
    guardarVistaPortal("carta");
  }, []);

  const irAConfirmacion = useCallback(() => {
    setVistaPortal("confirmacion");
    guardarVistaPortal("confirmacion");
  }, []);

  /* ================= CONFIRMACIONES ================= */

  async function confirmarDatosDomicilio(data) {
    const resultado = await finalizarPedido(data);
    if (resultado) {
      setMostrarModalDelivery(false);
      irAConfirmacion();
    }
  }

  async function confirmarDatosRetiro(data) {
    const resultado = await finalizarPedido(data);
    if (resultado) {
      setMostrarModalRetiro(false);
      irAConfirmacion();
    }
  }

  function manejarFinalizarPedido() {
    if (pedidosCerrados) {
      fireSwal(
        "Cerrado",
        mensajeCierre || "Estamos cerrados. Podrás pedir el próximo día de atención.",
        "info"
      );
      return;
    }

    const errorOpciones = validarCarritoOpciones(carrito);
    if (errorOpciones) {
      fireSwal("Error", errorOpciones, "error");
      return;
    }

    if (modoEntrega === "domicilio") {
      setMostrarModalDelivery(true);
      return;
    }

    if (modoEntrega === "retiro") {
      setMostrarModalRetiro(true);
      return;
    }

    fireSwal("Error", "Debes seleccionar retiro o delivery", "error");
  }

  async function manejarCambioSucursal(sucursalId) {
    if (!sucursalId || String(sucursalId) === String(sucursalSeleccionada?.id)) return;
    await seleccionarSucursalManual(sucursalId);
  }

  async function manejarSeleccionSucursal(sucursalId) {
    await manejarCambioSucursal(sucursalId);
    setMostrarModalSucursal(false);
  }

  async function manejarEditarComentario(idx) {
    const item = carrito[idx];
    if (!item) return;

    if (item.armable || item.con_tamanos) {
      const delCatalogo = productos.find(
        (prod) =>
          resolverProductoSucursalId(prod) === item.productoSucursalId
      );
      const productoEdicion = delCatalogo && productoRequiereSelector(delCatalogo)
        ? delCatalogo
        : {
            id: item.productoSucursalId,
            productoSucursalId: item.productoSucursalId,
            nombre: item.nombreBase || item.nombre,
            precio: item.precioBase != null ? item.precioBase : item.precio,
            imagen_url: item.imagen,
            categoria: item.categoria,
            descripcion: "",
            mostrar_extras: item.mostrar_extras === true,
            armable: Boolean(item.armable),
            armable_cantidad: item.armable_cantidad,
            ingredientes: item.ingredientes || [],
            ingredientes_con_precio: Boolean(item.ingredientes_con_precio),
            con_tamanos: Boolean(item.con_tamanos && item.tamanos?.length),
            tamanos: item.tamanos || [],
          };

      if (!productoRequiereSelector(productoEdicion)) {
        fireSwal(
          "Error",
          isProductoConTamanos(productoEdicion)
            ? `Elige el tamaño de ${item.nombreBase || item.nombre}`
            : `Elige los ingredientes de ${item.nombreBase || item.nombre}`,
          "error"
        );
        return;
      }

      setIndiceEdicionOpciones(idx);
      setProductoSeleccionado(productoEdicion);
      setModalAbierto(true);
      return;
    }

    const { isConfirmed, value } = await fireSwal({
      title: `Editar comentario de ${item.nombre}`,
      input: "textarea",
      inputLabel: "Comentario del item",
      inputValue: item.comentario || "",
      inputPlaceholder: "Ej: sin cebolla, bien fría, dejar en portería, etc.",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      inputAttributes: {
        maxlength: "250",
      },
    });

    if (!isConfirmed) return;
    actualizarComentario(idx, (value || "").trim());
  }

  function manejarAgregarProducto(producto, opciones) {
    if (indiceEdicionOpciones != null) {
      const itemActual = carrito[indiceEdicionOpciones];
      actualizarItemDesdeCatalogo(indiceEdicionOpciones, producto, {
        ...opciones,
        extras: itemActual?.extras || [],
      });
      cerrarModal();
      return;
    }
    for (const entrada of planAgregadoAlCarrito(producto, opciones)) {
      agregar(entrada.producto, entrada.opciones);
    }
  }

  /* ================= FILTRO ================= */

  const productosFiltrados = useMemo(() => {
    const termino = busqueda.toLowerCase();
    const categoriaNorm =
      categoriaActiva === "Todas" ? null : normalizarNombreCategoria(categoriaActiva);

    return productos.filter(
      (prod) =>
        (categoriaNorm == null ||
          normalizarNombreCategoria(prod.categoria) === categoriaNorm) &&
        prod.nombre.toLowerCase().includes(termino)
    );
  }, [productos, categoriaActiva, busqueda]);

  /* ================= RENDER ================= */

  if (estadoLocal !== "ok") {
    return (
      <EstadoLocalPortal
        estado={estadoLocal}
        slug={slug}
        error={errorLocal}
      />
    );
  }

  if (vistaPortal === "confirmacion" || vistaPortal === "estado") {
    return <PedidoConfirmado onVolverCarta={irACarta} />;
  }

  return (
    <>
      <CartaDigitalVista
        sucursalSeleccionada={sucursalSeleccionada}
        temaEstacional={temaEstacional}
        inicializandoSucursal={inicializandoSucursal}
        localNombre={local?.nombre}
        onLocalizarTienda={
          sucursales.length > 1
            ? () => setMostrarModalSucursal(true)
            : undefined
        }
        modoEntrega={modoEntrega}
        setModoEntrega={setModoEntrega}
        busqueda={busqueda}
        setBusqueda={setBusqueda}
        categoriaActiva={categoriaActiva}
        setCategoriaActiva={setCategoriaActiva}
        categoriasDisponibles={categoriasDisponibles}
        cargandoProductos={cargandoProductos}
        productosFiltrados={productosFiltrados}
        abrirModal={abrirModal}
        carrito={carrito}
        eliminar={eliminar}
        editar={manejarEditarComentario}
        cambiarCantidad={actualizarCantidad}
        vaciar={vaciar}
        costoDelivery={costoDelivery}
        horariosDisponibles={horariosDisponibles}
        horarioSeleccionado={horarioSeleccionado}
        setHorarioSeleccionado={setHorarioSeleccionado}
        pedidosCerrados={pedidosCerrados}
        mensajeCierre={mensajeCierre}
        finalizarPedido={manejarFinalizarPedido}
        enviandoPedido={enviandoPedido}
        productoSeleccionado={productoSeleccionado}
        modalAbierto={modalAbierto}
        cerrarModal={cerrarModal}
        agregar={manejarAgregarProducto}
        extrasDisponibles={extras}
        comentarioInicial={
          indiceEdicionOpciones != null
            ? carrito[indiceEdicionOpciones]?.comentario || ""
            : ""
        }
        modoEdicionOpciones={indiceEdicionOpciones != null}
      />

      {/* ===== MODAL DELIVERY ===== */}
      <ModalConfirmacionDomicilio
        abierto={mostrarModalDelivery}
        onCerrar={() => setMostrarModalDelivery(false)}
        ubicacionCliente={ubicacionCliente}
        onConfirmarDomicilio={confirmarDatosDomicilio}
      />

      {/* ===== MODAL RETIRO ===== */}
      <ModalRetiro
        abierto={mostrarModalRetiro}
        onCerrar={() => setMostrarModalRetiro(false)}
        onConfirmarRetiro={confirmarDatosRetiro}
      />

      {sucursales.length > 1 && (
        <ModalSucursal
          abierto={mostrarModalSucursal}
          onCerrar={() => setMostrarModalSucursal(false)}
          sucursales={sucursales}
          sucursalSeleccionada={sucursalSeleccionada}
          onSeleccionar={manejarSeleccionSucursal}
        />
      )}
    </>
  );
}
